import { AiEntityProposalType, AiFieldProposalStatus, ResolveAiFieldProposalInput } from "src/generated/graphqlTypes";
import { ObjectConfig, ObjectState } from "src/utils/formState";
import { AiFieldProposalFormValue, aiFieldProposalConfig } from "./aiFieldProposalForm";

/**
 * The bits every tab reviewing AI proposals needs, regardless of what it's reviewing.
 */

/** The shape of an `AiFieldProposal` as the server returns it, whatever fragment it came from. */
type ServerFieldProposal = {
  id: string;
  fieldName: string;
  fieldValue: string;
  status: { code: AiFieldProposalStatus };
};

/** One server proposal as the form holds it. `status.code` flattens to `status`. */
export function toAiFieldProposalFormValue(fp: ServerFieldProposal): AiFieldProposalFormValue {
  return { id: fp.id, fieldName: fp.fieldName, fieldValue: fp.fieldValue, status: fp.status.code };
}

/** Every field proposal under a set of entity proposals, flattened for a form's list field. */
export function flattenEntityProposals(
  entityProposals: readonly { fieldProposals: ServerFieldProposal[] }[] | null | undefined,
): AiFieldProposalFormValue[] {
  return (entityProposals ?? []).flatMap((aep) => aep.fieldProposals.map(toAiFieldProposalFormValue));
}

/** An `AiEntityProposal` as the server returns it. `entity` is a union, so only some members have an id. */
type ServerEntityProposal = {
  id: string;
  type: { code: AiEntityProposalType };
  entity?: ({ __typename?: string } & { id?: string }) | null;
  fieldProposals: ServerFieldProposal[];
};

/** One entity's proposal — what it wants done, and the fields it wants changed. */
export type EntityProposal = { aepId: string; type: AiEntityProposalType; fieldProposals: AiFieldProposalFormValue[] };

/**
 * Keys entity proposals by the id of the entity they're about, for tabs reviewing many entities at
 * once (a table of rooms, options, elevations…).
 *
 * `typename` narrows the `AiEntityProposalEntity` union — pass `"ReadyPlanLocation"`,
 * `"ReadyPlanOption"`, etc. Proposals whose entity is absent or of another type are dropped: there is
 * no row to attach them to. Note that even a CREATE proposal points at an entity
 * that already exists, so nothing legitimate is lost.
 */
export function indexEntityProposals(
  entityProposals: readonly ServerEntityProposal[] | null | undefined,
  typename: string,
): Map<string, EntityProposal> {
  return new Map(
    (entityProposals ?? []).flatMap((aep) => {
      const entityId = aep.entity?.__typename === typename ? aep.entity.id : undefined;
      return entityId
        ? [
            [
              entityId,
              {
                aepId: aep.id,
                type: aep.type.code,
                fieldProposals: aep.fieldProposals.map(toAiFieldProposalFormValue),
              },
            ] as const,
          ]
        : [];
    }),
  );
}

/**
 * The rows a save should send to `resolveAiFieldProposals`.
 *
 * Takes `changedValue.fieldProposals`, so rows the user never touched are left out.
 */
export function toResolveInputs(
  rows: (Partial<AiFieldProposalFormValue> | undefined)[] | undefined,
): ResolveAiFieldProposalInput[] {
  return (rows ?? []).flatMap((row) => (row?.id && row.status ? [{ id: row.id, status: row.status }] : []));
}

export function hasPendingProposals(rows: readonly ObjectState<AiFieldProposalFormValue>[]): boolean {
  return rows.some((row) => row.status.value === AiFieldProposalStatus.Pending);
}

/**
 * A form whose only job is reviewing proposals, for tabs that can't nest them on the form they're
 * reviewing. See `LocationsTab` for why that happens with `useFormStates`.
 */
export type AiProposalsFormValue = { fieldProposals: AiFieldProposalFormValue[] };

export const aiProposalsConfig: ObjectConfig<AiProposalsFormValue> = {
  fieldProposals: { type: "list", update: "incremental", config: aiFieldProposalConfig },
};
